import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { ChevronDown, X } from 'lucide-react';

import FooterContainer from '../FooterContainer/FooterContainer';
import { LayoutComposer, StaticPage } from '../PageBuilder/PageBuilder';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import CategoryBar from '../ResalePage/CategoryBar';

import { ReviewGrid, ReviewsFilterDrawer, ReviewsSubHeader } from './components/index.js';
import {
  fetchAvailableReviewFilterOptions,
  getAvailableReviewFilterOptions,
  getAvailableReviewFilterOptionsInProgress,
  getAvailableReviewFilterOptionsQueryKey,
  getLastReviewsQueryParams,
  getReviews,
  getReviewsError,
  getReviewsInProgress,
  getReviewsPagination,
  selectCurrentPage,
} from './ReviewsPage.duck';
import useRevealOnReady from '../../hooks/useRevealOnReady';
import { addInchesToHeight, formatHeightRange } from '../../util/string';
import { stringify } from '../../util/urlHelpers';
import { CLOTHING_SIZES, SHOE_SIZES } from '../../util/size';
import buttonCss from '../LandingPage/components/HomepageButtons.module.css';

import css from './ReviewsPage.module.css';
import revealCss from '../../styles/contentReveal.module.css';

const HEIGHT_OPTIONS = [
  '<5\'0"',
  '5\'1" - 5\'2"',
  '5\'3" - 5\'4"',
  '5\'5" - 5\'6"',
  '5\'7" - 5\'8"',
  '5\'9" - 5\'10"',
  '5\'11" - 6\'0"',
].map(label => ({
  label,
  value: formatHeightRange(label),
}));

const TYPE_OPTIONS = [
  { value: 'all', label: 'Category' },
  { value: 'clothing', label: 'Clothing' },
  { value: 'shoes', label: 'Shoes' },
  { value: 'bags', label: 'Bags' },
  { value: 'jewelry', label: 'Jewelry' },
  { value: 'accessories', label: 'Accessories' },
  { value: 'other', label: 'Other' },
];

const ensureActiveOption = (options, activeValue, createFallbackOption) => {
  if (!activeValue || options.some(option => option.value === activeValue)) {
    return options;
  }

  return [...options, createFallbackOption(activeValue)];
};

const ReviewsPageComponent = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const currentPage = useSelector(selectCurrentPage);
  const reviews = useSelector(getReviews);
  const reviewsInProgress = useSelector(getReviewsInProgress);
  const reviewsError = useSelector(getReviewsError);
  const pagination = useSelector(getReviewsPagination);
  const lastQueryParams = useSelector(getLastReviewsQueryParams);
  const availableReviewFilterOptions = useSelector(getAvailableReviewFilterOptions);
  const availableReviewFilterOptionsInProgress = useSelector(
    getAvailableReviewFilterOptionsInProgress
  );
  const availableReviewFilterOptionsQueryKey = useSelector(getAvailableReviewFilterOptionsQueryKey);
  const [isSizeHintVisible, setIsSizeHintVisible] = useState(false);
  const sizeHintRef = useRef(null);
  const sizeHintBubbleRef = useRef(null);
  const sizeHintPointerTypeRef = useRef(null);
  const [sizeHintPosition, setSizeHintPosition] = useState({ top: 0, left: 16, width: 220 });

  const schemaTitle = 'Reviews';
  const schemaDescription = 'Browse and add reviews for marketplace listings';

  const filterParams = useMemo(
    () => Object.fromEntries(new URLSearchParams(location.search).entries()),
    [location.search]
  );

  const currentQueryParams = useMemo(() => ({ page: currentPage, ...filterParams }), [
    currentPage,
    filterParams,
  ]);
  const filterAvailabilityQueryParams = useMemo(() => {
    const params = { ...filterParams };
    delete params.page;
    return params;
  }, [filterParams]);
  const currentQueryKey = useMemo(() => stringify(currentQueryParams), [currentQueryParams]);
  const lastQueryKey = useMemo(() => stringify(lastQueryParams || {}), [lastQueryParams]);
  const filterAvailabilityQueryKey = useMemo(() => stringify(filterAvailabilityQueryParams), [
    filterAvailabilityQueryParams,
  ]);
  const activeCategory = filterParams.pub_categoryLevel1 || 'all';
  const activeHeight = filterParams.pub_height || '';
  const activeBrandSlug = filterParams.pub_brandSlug || '';
  const activeBrandName = filterParams.brandName || '';
  const activeSize = filterParams.pub_ClothingSize || filterParams.pub_ShoeSize || '';
  const inferredCategory =
    activeCategory === 'all' && availableReviewFilterOptions.categories.length === 1
      ? availableReviewFilterOptions.categories[0]
      : activeCategory;
  const sizeFilterType = filterParams.pub_ShoeSize
    ? 'shoes'
    : filterParams.pub_ClothingSize
    ? 'clothing'
    : inferredCategory;
  const isSizeFilterEnabled = sizeFilterType === 'clothing' || sizeFilterType === 'shoes';
  const hasActiveFilters = Boolean(
    activeHeight ||
      activeBrandSlug ||
      activeBrandName ||
      activeSize ||
      (activeCategory && activeCategory !== 'all')
  );
  const hasResolvedCurrentQuery =
    currentQueryKey === lastQueryKey &&
    (reviews.length > 0 || pagination !== null || Boolean(reviewsError));
  const hasRevealedContent = useRevealOnReady(hasResolvedCurrentQuery);
  const revealedContentClassName = classNames(revealCss.reveal, {
    [revealCss.hidden]: !hasRevealedContent,
    [revealCss.visible]: hasRevealedContent,
  });

  useEffect(() => {
    dispatch(fetchAvailableReviewFilterOptions(filterAvailabilityQueryParams));
  }, [dispatch, filterAvailabilityQueryParams]);

  useEffect(() => {
    if (isSizeFilterEnabled) {
      setIsSizeHintVisible(false);
    }
  }, [isSizeFilterEnabled]);

  useEffect(() => {
    if (!isSizeHintVisible) {
      return undefined;
    }

    const updateSizeHintPosition = () => {
      const shellRect = sizeHintRef.current?.getBoundingClientRect();
      const bubbleHeight = sizeHintBubbleRef.current?.offsetHeight || 54;

      if (!shellRect) {
        return;
      }

      const bubbleWidth = Math.min(shellRect.width, window.innerWidth - 32);
      const left = Math.max(16, Math.min(shellRect.left, window.innerWidth - bubbleWidth - 16));

      setSizeHintPosition({
        top: Math.max(16, shellRect.top - bubbleHeight - 10),
        left,
        width: bubbleWidth,
      });
    };

    updateSizeHintPosition();
    const frameId = window.requestAnimationFrame(updateSizeHintPosition);

    const handlePointerDown = event => {
      if (sizeHintRef.current && !sizeHintRef.current.contains(event.target)) {
        setIsSizeHintVisible(false);
      }
    };

    const handleScroll = () => {
      setIsSizeHintVisible(false);
    };

    window.addEventListener('resize', updateSizeHintPosition);
    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', updateSizeHintPosition);
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [isSizeHintVisible]);

  const updateReviewParams = useCallback(
    updateFn => {
      const nextParams = new URLSearchParams(location.search);
      updateFn(nextParams);
      nextParams.delete('page');

      const nextSearch = nextParams.toString();
      history.replace(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`);
    },
    [history, location.pathname, location.search]
  );

  const handleHeightChange = useCallback(
    event => {
      const { value } = event.target;
      updateReviewParams(params => {
        if (value) {
          params.set('pub_height', value);
        } else {
          params.delete('pub_height');
        }
      });
    },
    [updateReviewParams]
  );

  const handleTypeChange = useCallback(
    event => {
      const { value } = event.target;
      updateReviewParams(params => {
        if (value && value !== 'all') {
          params.set('pub_categoryLevel1', value);
        } else {
          params.delete('pub_categoryLevel1');
        }

        if (value !== 'clothing') {
          params.delete('pub_ClothingSize');
        }

        if (value !== 'shoes') {
          params.delete('pub_ShoeSize');
        }
      });
    },
    [updateReviewParams]
  );

  const handleSizeChange = useCallback(
    event => {
      const { value } = event.target;
      updateReviewParams(params => {
        params.delete('pub_ClothingSize');
        params.delete('pub_ShoeSize');

        if (!value) {
          return;
        }

        if (sizeFilterType === 'shoes') {
          params.set('pub_categoryLevel1', 'shoes');
          params.set('pub_ShoeSize', value);
        } else {
          params.set('pub_categoryLevel1', 'clothing');
          params.set('pub_ClothingSize', value);
        }
      });
    },
    [sizeFilterType, updateReviewParams]
  );

  const handleClearFilters = useCallback(() => {
    history.replace(location.pathname);
  }, [history, location.pathname]);

  const handleSizeHintToggle = useCallback(() => {
    if (!isSizeFilterEnabled) {
      setIsSizeHintVisible(currentValue => !currentValue);
    }
  }, [isSizeFilterEnabled]);

  const handleSizeHintMouseEnter = useCallback(() => {
    if (!isSizeFilterEnabled) {
      setIsSizeHintVisible(true);
    }
  }, [isSizeFilterEnabled]);

  const handleSizeHintMouseLeave = useCallback(() => {
    if (!isSizeFilterEnabled) {
      setIsSizeHintVisible(false);
    }
  }, [isSizeFilterEnabled]);

  const handleSizeHintPress = useCallback(
    event => {
      const pointerType = event.pointerType || sizeHintPointerTypeRef.current;
      sizeHintPointerTypeRef.current = pointerType || null;

      if (isSizeFilterEnabled || pointerType === 'mouse') {
        return;
      }

      event.preventDefault();
      setIsSizeHintVisible(currentValue => !currentValue);
    },
    [isSizeFilterEnabled]
  );

  const heightOptions = useMemo(() => {
    const availableHeightValues = new Set(availableReviewFilterOptions.heights);
    const filteredOptions = HEIGHT_OPTIONS.filter(option =>
      availableHeightValues.has(option.value)
    );

    return ensureActiveOption(filteredOptions, activeHeight, value => ({
      value,
      label: addInchesToHeight(value),
    }));
  }, [activeHeight, availableReviewFilterOptions.heights]);

  const typeOptions = useMemo(() => {
    const filteredOptions = TYPE_OPTIONS.filter(
      option =>
        option.value === 'all' || availableReviewFilterOptions.categories.includes(option.value)
    );

    return ensureActiveOption(filteredOptions, activeCategory, value => ({
      value,
      label: TYPE_OPTIONS.find(option => option.value === value)?.label || value,
    }));
  }, [activeCategory, availableReviewFilterOptions.categories]);

  const sizeOptions = useMemo(() => {
    const baseOptions = sizeFilterType === 'shoes' ? SHOE_SIZES : CLOTHING_SIZES;
    const filteredOptions =
      sizeFilterType === 'shoes'
        ? availableReviewFilterOptions.shoeSizes
        : availableReviewFilterOptions.clothingSizes;

    return ensureActiveOption(filteredOptions, activeSize, value => {
      const fallback = baseOptions.find(option => option.value === value);
      return fallback || { value, label: value };
    });
  }, [
    activeSize,
    availableReviewFilterOptions.clothingSizes,
    availableReviewFilterOptions.shoeSizes,
    sizeFilterType,
  ]);

  const activeBrandValue = useMemo(() => {
    if (activeBrandSlug) {
      return activeBrandSlug;
    }

    if (activeBrandName) {
      const matchingOption = availableReviewFilterOptions.brands.find(
        option => option.queryParam === 'brandName' && option.queryValue === activeBrandName
      );

      return matchingOption?.value || `brand-name:${activeBrandName}`;
    }

    return '';
  }, [activeBrandName, activeBrandSlug, availableReviewFilterOptions.brands]);

  const brandOptions = useMemo(() => {
    const filteredOptions = availableReviewFilterOptions.brands;

    return ensureActiveOption(filteredOptions, activeBrandValue, value => ({
      value,
      label: activeBrandName || value,
      queryParam: activeBrandName ? 'brandName' : 'pub_brandSlug',
      queryValue: activeBrandName || value,
    }));
  }, [activeBrandName, activeBrandValue, availableReviewFilterOptions.brands]);

  const handleBrandChange = useCallback(
    event => {
      const { value } = event.target;
      const selectedOption = brandOptions.find(option => option.value === value);
      updateReviewParams(params => {
        params.delete('pub_brandSlug');
        params.delete('brandName');

        if (!value) {
          return;
        }

        if (selectedOption?.queryParam === 'brandName') {
          params.set('brandName', selectedOption.queryValue || selectedOption.label);
          return;
        }

        params.set('pub_brandSlug', selectedOption?.queryValue || value);
      });
    },
    [brandOptions, updateReviewParams]
  );

  const layoutAreas = `
    topbar
    main
    footer
  `;
  const sizeHintBubble =
    typeof document !== 'undefined' && !isSizeFilterEnabled
      ? createPortal(
          <div
            ref={sizeHintBubbleRef}
            id="reviews-size-filter-hint"
            role="status"
            style={
              isSizeHintVisible
                ? {
                    ...sizeHintPosition,
                    visibility: 'visible',
                    display: 'block',
                  }
                : undefined
            }
            className={classNames(css.filterHintBubble, {
              [css.filterHintBubbleVisible]: isSizeHintVisible,
            })}
          >
            Please select a category <span className={css.filterHintNoWrap}>to enable size.</span>
          </div>,
          document.body
        )
      : null;

  return (
    <StaticPage
      title={schemaTitle}
      description={schemaDescription}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        description: schemaDescription,
      }}
    >
      <LayoutComposer areas={layoutAreas}>
        {props => {
          const { Topbar, Main, Footer } = props;
          return (
            <>
              <Topbar as="header">
                <TopbarContainer />
              </Topbar>
              <Main as="main" className={classNames(css.main, revealedContentClassName)}>
                <div className={css.container}>
                  <div className={css.header}>
                    <div className={css.reviewsTitle}>
                      <h1 className={css.pageTitle}>Reviews</h1>
                    </div>

                    <div className={css.inlineSubHeader}>
                      <ReviewsSubHeader />
                    </div>
                  </div>

                  <div className={css.categoryBarWrap}>
                    <CategoryBar
                      activeCategory={activeCategory}
                      onCategoryChange={() => {}}
                      contentClassName={css.categoryBarInnerCompact}
                    >
                      <div className={css.filterBarContent}>
                        <div className={css.mobileFilterDrawer}>
                          <ReviewsFilterDrawer />
                        </div>

                        <div className={css.inlineFilters}>
                          <div className={css.filterControl}>
                            <div className={css.filterSelectShell}>
                              <select
                                className={css.filterSelect}
                                aria-label="Category"
                                value={inferredCategory}
                                onChange={handleTypeChange}
                              >
                                {typeOptions.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown
                                className={css.filterSelectIcon}
                                aria-hidden="true"
                                size={12}
                              />
                            </div>
                          </div>

                          <div className={css.filterControl}>
                            <div
                              className={css.filterSelectShell}
                              ref={sizeHintRef}
                              onMouseEnter={handleSizeHintMouseEnter}
                              onMouseLeave={handleSizeHintMouseLeave}
                            >
                              <select
                                className={css.filterSelect}
                                aria-label="Size"
                                aria-describedby={
                                  !isSizeFilterEnabled ? 'reviews-size-filter-hint' : undefined
                                }
                                value={activeSize}
                                onChange={handleSizeChange}
                                disabled={!isSizeFilterEnabled}
                              >
                                <option value="">Size</option>
                                {sizeOptions.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown
                                className={css.filterSelectIcon}
                                aria-hidden="true"
                                size={12}
                              />
                              {!isSizeFilterEnabled ? (
                                <>
                                  <button
                                    type="button"
                                    className={css.filterHintOverlay}
                                    aria-label="Why is size disabled?"
                                    aria-controls="reviews-size-filter-hint"
                                    aria-expanded={isSizeHintVisible}
                                    onPointerDown={handleSizeHintPress}
                                  />
                                </>
                              ) : null}
                            </div>
                          </div>

                          <div className={`${css.filterControl} ${css.filterControlBrand}`}>
                            <div className={css.filterSelectShell}>
                              <select
                                className={css.filterSelect}
                                aria-label="Brand"
                                value={activeBrandValue}
                                onChange={handleBrandChange}
                              >
                                <option value="">Brand</option>
                                {brandOptions.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown
                                className={css.filterSelectIcon}
                                aria-hidden="true"
                                size={12}
                              />
                            </div>
                          </div>

                          <div className={css.filterControl}>
                            <div className={css.filterSelectShell}>
                              <select
                                className={css.filterSelect}
                                aria-label="Height"
                                value={activeHeight}
                                onChange={handleHeightChange}
                              >
                                <option value="">Height</option>
                                {heightOptions.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown
                                className={css.filterSelectIcon}
                                aria-hidden="true"
                                size={12}
                              />
                            </div>
                          </div>

                          <div className={css.filterActions}>
                            <button
                              type="button"
                              className={`${css.clearFiltersButton} ${buttonCss.button} ${buttonCss.buttonSecondary} ${buttonCss.buttonLight}`}
                              onClick={handleClearFilters}
                              disabled={!hasActiveFilters}
                            >
                              <X
                                className={`${buttonCss.icon} ${css.clearFiltersIcon}`}
                                aria-hidden="true"
                                size={10}
                                strokeWidth={2}
                                absoluteStrokeWidth
                              />
                              CLEAR
                            </button>
                          </div>
                        </div>
                      </div>
                    </CategoryBar>
                  </div>

                  <ReviewGrid />
                </div>
              </Main>
              <Footer className={revealedContentClassName}>
                <FooterContainer />
              </Footer>
              {sizeHintBubble}
            </>
          );
        }}
      </LayoutComposer>
    </StaticPage>
  );
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
const ReviewsPage = compose(withRouter)(ReviewsPageComponent);

export { ReviewsPageComponent };
export default ReviewsPage;
